import { Controller } from "react-hook-form";

import { FormDialog } from "ui/form-dialog";
import { TextField } from "ui/text-field";
import { Switch } from "ui/switch";
import { Select } from "ui/select";
import CreativeFormFields from "components/modals/creative-modal/components/creative-form-fields";

import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";

import { TouchpointType } from "types/touchpoint";

import { useCloneTouchpointModal } from "components/modals/clone-touchpoint-modal/use-clone-touchpoint-modal";
import { getActionTouchpointNameLabel, getActionTouchpointTypeLabel } from "utilities";

import Icons from "assets";

interface CloneTouchpointModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedVersion?: TouchpointVersionAttributes;
  onSuccess?: () => void;
}

export const CloneTouchpointModal = ({
  isOpen,
  onClose,
  selectedVersion,
  onSuccess,
}: CloneTouchpointModalProps) => {
  const {
    methods,
    control,
    errors,
    isValid,
    isPending,
    campaignsOptions,
    contentTypeOptions,
    populationTypeOptions,
    fyOptions,
    selectedCampaign,
    currentTouchpoint,
    inputRef,
    handleClose,
    handleSubmitCloneTouchpoint,
    onCampaignChange,
    contentTypes,
    classTypeOptions,
  } = useCloneTouchpointModal({
    isOpen,
    onClose,
    selectedVersion,
    onSuccess,
  });

  if (!isOpen) return null;

  const touchpointLabel = getActionTouchpointTypeLabel(currentTouchpoint.type).toLowerCase();

  return (
    <FormDialog
      open
      onClose={handleClose}
      closable={!isPending}
      headline={`Clone ${touchpointLabel}`}
      form={methods}
      onSubmit={handleSubmitCloneTouchpoint}
      submitDisabled={!isValid || campaignsOptions.length === 0}
      submitting={isPending}
      submitButtonText={`Clone ${touchpointLabel}`}
      submitLoadingMessage="Cloning...">
      <Controller
        name="campaignId"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            label="Campaign name"
            placeholder="Select..."
            ariaLabel="Choose campaign"
            selectName={`campaigns-select-${field.name}`}
            options={campaignsOptions}
            required
            startIcon={<Icons.Search className="text-aqua-600" />}
            value={campaignsOptions.find((option) => option.value === field.value)}
            onChange={(option) => onCampaignChange(option, field.onChange)}
            errMessage={errors.campaignId?.message}
          />
        )}
      />
      <Controller
        name="touchpointName"
        control={control}
        render={({ field }) => (
          <TextField
            label={getActionTouchpointNameLabel(currentTouchpoint.type)}
            required
            errMessage={errors.touchpointName?.message}
            {...field}
            ref={inputRef}
          />
        )}
      />
      <CreativeFormFields
        populationTypeOptions={populationTypeOptions}
        contentTypeOptions={contentTypeOptions}
        classTypeOptions={classTypeOptions}
        fyOptions={fyOptions}
        isContentTypeLocked={contentTypes?.length === 1}
        resetKey={selectedCampaign.id}>
        {currentTouchpoint.type === TouchpointType.EMAIL && (
          <Controller
            name="applyClientTheme"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Switch toggleName="Enable client theme" checked={value} onChange={onChange} />
            )}
          />
        )}
      </CreativeFormFields>
    </FormDialog>
  );
};
